import React, { useState } from 'react';

import { Card, Image, Modal, Popconfirm, Table, Tooltip } from 'antd';
import { DeleteOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';

import MediaThumb from './AddMediaContent/MediaThumb';
import { isPreviewableMedia, resolvePosterUrl } from './AddMediaContent/helpers';

/**
 * Bir dosya satırının tam-boy görsel olarak (lightbox'ta) önizlenebilir olup olmadığı.
 * Kural `isPreviewableMedia`'da tek kaynaktan gelir; buradaki sarmalayıcı yalnız tablo
 * satırının `{ mediaType, media }` şeklini açar. Göz aksiyonunun görünürlüğü ile önizleme
 * grubuna dahil edilme aynı fonksiyondan türer; aksi halde ikisi sessizce ayrışabilirdi.
 *
 * @param {{ mediaType?: string, media?: { name?: string, fileFullUrl?: string } }} record
 * @returns {boolean}
 */
const isPreviewableImage = (record) => isPreviewableMedia(record?.media, record?.mediaType);

/**
 * "Dosya Yönetimi" modalı — bir medya teklifi grubundaki dosyaları listeler.
 *
 * Her satır: solda küçük önizleme (`MediaThumb` — resimde thumbnail, resim olmayan
 * türde veya kırık görselde tür ikonu), ortada dosya adı, sağda aksiyon ikonları.
 * Sağdaki aksiyonlar:
 *  - **Büyük görüntüle** (göz, 👁): yalnız önizlenebilir görsel satırlarında (`isPreviewableImage`)
 *    çıkar; tıklanınca dosyayı tam ekran lightbox'ta (zoom/döndür/gezinme) açar. Gruptaki tüm
 *    önizlenebilir görseller tek bir `Image.PreviewGroup`'a kayıtlıdır → lightbox içinde `‹ ›`
 *    ile aralarında gezilir.
 *  - **İndirme** (⬇) ya da **silme** (🗑): `isContentSend`'e göre biri. İnceleme/görüntüleme
 *    bağlamında indirme, yazar gönderme akışında silme.
 *
 * Not: Dosyaların kendi `media` nesnesi medya türünü taşımaz (tür grup seviyesindedir),
 * bu yüzden çağıran taraf her satıra grubun `mediaType`'ını iliştirerek geçirir.
 *
 * @param {Object} props
 * @param {Array<{ id: string|number, mediaType?: string, media: { name: string, fileFullUrl: string, thumbnailFullUrl?: string } }>} props.data
 *   - Listelenecek dosyalar (grubun `mediaOfferItems`'ı; her öğe grup `mediaType`'ı ile zenginleştirilmiş).
 * @param {boolean} props.open - Modal açık mı.
 * @param {() => void} props.onClose - Modalı kapatır.
 * @param {(url: string, fileName: string) => void} props.downloadBlob - Dosyayı indirir (yalnız `isContentSend` iken).
 * @param {(id: string|number) => void} props.handleDeleteFormMedia - Dosyayı siler (yalnız `!isContentSend` iken).
 * @param {boolean} props.isContentSend - true → sağ aksiyon indirme; false → sağ aksiyon silme.
 */
const UploadedMediaModal = ({ data, open, onClose, downloadBlob, handleDeleteFormMedia, isContentSend }) => {
  // Büyük görüntüleme (lightbox) durumu. `current`, önizlenebilir görsellerin (previewItems)
  // mount/kayıt sırasındaki index'idir — `Image.PreviewGroup` bu index ile hangi görselin açık
  // olacağını belirler. Kapanışta rc-image kontrollü index'e geri döner (aynı satırı yeniden
  // açmak sorunsuz çalışır), bu yüzden gezinme sonrası current'ı ayrıca takip etmemize gerek yok.
  const [preview, setPreview] = useState({ visible: false, current: 0 });

  // Gruptaki önizlenebilir görseller; göz butonundan açılacak lightbox grubunun sırasını belirler.
  // Aynı listeyi hem index çözümünde hem gizli <Image> kaydında kullanıyoruz ki sıra hizası korunur.
  const previewItems = (data || []).filter(isPreviewableImage);

  const openPreview = (record) => {
    const index = previewItems.findIndex((item) => item.id === record.id);
    if (index >= 0) setPreview({ visible: true, current: index });
  };

  const fileColumn = {
    title: 'Madde Adı',
    dataIndex: ['media', 'name'],
    key: 'id',
    render: (text, record) => (
      <div className="gx-d-flex gx-align-items-center">
        <MediaThumb
          src={resolvePosterUrl(record.media, record.mediaType)}
          mediaType={record.mediaType}
          size={48}
          alt={text}
          bordered
        />
        <span className="gx-ml-3">{text}</span>
      </div>
    ),
  };

  // Sağ-aksiyon kolonu: önizlenebilir görsellerde önce "büyük görüntüle" (göz), ardından bağlama
  // göre indirme ya da silme. Her aksiyon kendi ikonuna bağlıdır; tıklama alanları ayrıktır.
  const actionColumn = {
    key: 'action',
    width: 96,
    align: 'right',
    render: (_, record) => (
      <div
        className="gx-d-flex gx-align-items-center gx-justify-content-end"
        style={{ gap: 16 }}
      >
        {isPreviewableImage(record) && (
          <Tooltip title="Büyük görüntüle">
            <EyeOutlined
              style={{ cursor: 'pointer' }}
              aria-label="Büyük görüntüle"
              onClick={() => openPreview(record)}
            />
          </Tooltip>
        )}
        {isContentSend ? (
          <Tooltip title="İndir">
            <DownloadOutlined
              style={{ cursor: 'pointer' }}
              aria-label="İndir"
              onClick={() => downloadBlob(record.media?.fileFullUrl, record.media?.name)}
            />
          </Tooltip>
        ) : (
          <Popconfirm
            placement="leftBottom"
            title="Silmek İstediğinizden Emin misiniz?"
            okText="Evet"
            onConfirm={() => handleDeleteFormMedia(record.id)}
          >
            <Tooltip title="Sil">
              <DeleteOutlined style={{ cursor: 'pointer' }} />
            </Tooltip>
          </Popconfirm>
        )}
      </div>
    ),
  };

  return (
    <Modal
      title="Dosya Yönetimi"
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
    >
      <Card>
        <Table
          rowKey="id"
          dataSource={data}
          columns={[fileColumn, actionColumn]}
          pagination={data.length > 10}
        />
      </Card>
      {/**
       * Kontrollü önizleme grubu. Gizli tutulur (thumbnail'lar zaten tabloda `MediaThumb` ile
       * çiziliyor); yalnız göz butonunun açtığı lightbox'ı besler. Lightbox rc-dialog ile body'e
       * portallandığı için `display:none` sarmalayıcı yalnız kayıt amaçlı <img>'leri gizler,
       * tam ekran önizlemeyi değil. `src` tam-boy `fileFullUrl`'dir (thumbnail/poster değil).
       */}
      <div style={{ display: 'none' }}>
        <Image.PreviewGroup
          preview={{
            visible: preview.visible,
            current: preview.current,
            onVisibleChange: (visible) => setPreview((prev) => ({ ...prev, visible })),
          }}
        >
          {previewItems.map((item) => (
            <Image
              key={item.id}
              src={item.media.fileFullUrl}
              alt={item.media?.name}
            />
          ))}
        </Image.PreviewGroup>
      </div>
    </Modal>
  );
};

export default UploadedMediaModal;
